
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import './StartPage.css';

const header3Definition = [
  'Datum & Uhrzeit',
  'Zusammenfassung',
  'Bestätigung',
];
const header5Definition = [
  'Behandlung',
  'Behandler',
  'Datum & Uhrzeit',
  'Zusammenfassung',
  'Bestätigung',
];
const header5NewUser = [
  'Behandlung',
  'Behandler',
  'Datum & Uhrzeit',
  'Persönliche Angaben',
  'Bestätigung',
];
export const header3DefinitionJSON = JSON.stringify(header3Definition);
export const header5DefinitionJSON = JSON.stringify(header5Definition);
export const header5NewUserJSON = JSON.stringify(header5NewUser);

export function StartPage() {
  const pageRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [version, setVersion] = useState('');

  useLayoutEffect(() => {
    const page = pageRef.current;
    const choices = page?.querySelector<HTMLElement>('.start-page__choices');
    const footer = page?.querySelector<HTMLElement>('.start-page__footer');
    if (!page || !choices || !footer) return;
    let active = true;
    const updatePosition = () => {
      if (!active) return;
      // Relative positioning keeps the original footer and background geometry.
      const currentShift = parseFloat(getComputedStyle(choices).top) || 0;
      const originalBottom = choices.getBoundingClientRect().bottom - currentShift;
      const footerTextTop = footer.getBoundingClientRect().top + (parseFloat(getComputedStyle(footer).paddingTop) || 0);
      const shift = Math.floor(Math.max(0, Math.min(100, footerTextTop - originalBottom - 20)) * 100) / 100;
      const value = `${shift}px`;
      if (page.style.getPropertyValue('--start-page-vertical-shift') !== value) page.style.setProperty('--start-page-vertical-shift', value);
    };
    updatePosition();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updatePosition);
    [page, choices, footer].forEach((element) => observer?.observe(element));
    window.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('resize', updatePosition);
    void document.fonts?.ready.then(updatePosition);
    return () => {
      active = false;
      observer?.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('resize', updatePosition);
    };
  }, []);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/version`);
        if (!response.ok) return;
        const data = await response.json();
        if (typeof data?.version === 'string') {
          setVersion(data.version);
        }
      } catch {
        // Ignore version load failures.
      }
    };

    fetchVersion();
  }, []);

  const handleNewPatient = () => {
    console.log('New patient');
    localStorage.setItem('headerDefinition', header5NewUserJSON);
    localStorage.setItem('selectedOfficeId', 'data_c_office_id');
    localStorage.setItem('loginPatientId', '');
    localStorage.setItem('loginUserName', '');
    localStorage.setItem('userFullName', '');
    localStorage.setItem('selectedPractitionerId', '');
    localStorage.setItem('selectedAppointmentId', '');
    localStorage.setItem('previousPage', '');
    navigate('/procedures');
  };

  const handleExistingPatient = () => {
    localStorage.setItem('headerDefinition', header3DefinitionJSON);
    localStorage.setItem('selectedOfficeId', 'data_c_office_id');
    localStorage.setItem('selectedPractitionerId', '');
    localStorage.setItem('selectedAppointmentId', '');
    localStorage.setItem('previousPage', '');
    navigate('/login');
  };

  const impressum = () => {

  };

  const contact = () => {

  };

  return (
    <main ref={pageRef} className="start-page" aria-labelledby="start-page-title">
      <div className="start-page__visual" aria-hidden="true">
        <img src="/images/StartImage.jpg" alt="" className="start-page__image" />
      </div>

      <header className="start-page__header">
        <img src="/images/offices/Thomas/logo.svg" alt="Praxis Logo" className="start-page__logo" />
        <p className="start-page__practice-name">
          <span>Zahnarztpraxis</span>
          <span>Müller und Weidmann</span>
        </p>
      </header>

      <section className="start-page__content" aria-labelledby="start-page-title">
        <div className="start-page__intro">
          <h1 id="start-page-title" className="start-page__title">
            <span>Online-Terminbuchung</span>
          </h1>
        </div>

        <div className="start-page__choices">
          <button type="button" onClick={handleNewPatient} className="start-page__card">
            <span className="start-page__card-icon">
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M15 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM6 10V7H4v3H1v2h3v3h2v-3h3v-2H6Zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4Z" />
              </svg>
            </span>
            <span className="start-page__card-copy">
              <span className="start-page__card-title">Neu in der Praxis</span>
              <span className="start-page__card-description">Ersten Termin vereinbaren.</span>
            </span>
          </button>

          <button type="button" onClick={handleExistingPatient} className="start-page__card">
            <span className="start-page__card-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" aria-hidden="true">
                <path d="M4 12h15M12 5l7 7-7 7" />
              </svg>
            </span>
            <span className="start-page__card-copy">
              <span className="start-page__card-title">Bereits Patient</span>
              <span className="start-page__card-description">Termine und Dokumente verwalten.</span>
            </span>
          </button>
        </div>
      </section>

      <footer className="start-page__footer">
        <p title={version ? `RiviDent (${version})` : undefined}>
          powered by <span>RiviDent</span>
        </p>
        <span aria-hidden="true">|</span>
        <Link
          to="/datenschutz"
          state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }}
        >
          Datenschutz
        </Link>
        <span aria-hidden="true">|</span>
        <button type="button" onClick={impressum}>
          Impressum
        </button>
        <span aria-hidden="true">|</span>
        <button type="button" onClick={contact}>
          Kontakt
        </button>
      </footer>
    </main>
  );
}
